import {useContext, useState} from 'react';

import {pdf} from '@react-pdf/renderer';
import {useTranslation} from 'react-i18next';

import {Grid, styled, TableCell, Typography} from '@mui/material';

import {ScreeningApplicantPDFReport} from '../components/PDFReport/ScreeningApplicantPdfReport';
import {serializeApplicantScreeningReport} from '../serializers/serializeApplicantScreeningReport';
import {ScreeningApplicant, ScreeningApplicantTalentSurvey} from '../types/screeningExternalApplicantsTypes';

import {CultureFit} from './components/CultureFit';
import {ScreeningBurgerActions} from './ScreeningBurgerActions';
import {BasicTableRow} from '../../../../components/BasicTable/BasicTableRow';
import {EmpionButton} from '../../../../components/EmpionButton/EmpionButton';
import {Icon, IconType} from '../../../../components/Icon/Icon';
import {TextOverflowWithTooltip} from '../../../../components/TextOverflowWithTooltip/TextOverflowWithTooltip';
import {CompanyContext} from '../../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiRead} from '../../../../hooks/useApiRead';
import {GetRoutes} from '../../../../lib/api/GET/GetRoutes';
import {ApplicantScreeningReport} from '../../../../types/ApplicantScreeningReport';
import {calculateDuration, transformCurrentDateToDDMMYYYY} from '../../../../utils/dateUtils/dateFormattingUtils';

interface ApplicantsScreeningRowProps extends ScreeningApplicant {
  refetch: () => void;
}

const StyledCell = styled(TableCell)(() => ({
  paddingTop: '11px',
  paddingBottom: '11px',
  maxWidth: '210px',
}));

export const ApplicantsScreeningRow = ({
  id,
  firstName,
  lastName,
  email,
  sendViaEmail,
  jobPosting,
  location,
  companyDepartmentId,
  talentSurvey,
  refetch,
}: ApplicantsScreeningRowProps) => {
  const {t: tr} = useTranslation('translation', {keyPrefix: 'screeningModule.tabFilters'});
  const {t} = useTranslation();

  const [loading, setLoading] = useState(false);

  const {company} = useContext(CompanyContext);
  const {dispatchMessage} = useContext(SnackBarContext);
  const {refetch: fetchApplicant} = useApiRead<ApplicantScreeningReport>({
    path: GetRoutes.screeningApplicantReport(company.data?.resourceId || '', id),
    camelCaseOverride: serializeApplicantScreeningReport,
    options: {enabled: false},
  });

  const currentLanguage = useContext(LanguageContext);
  const daysElapsed = calculateDuration(talentSurvey.updatedAt, new Date(), currentLanguage) || '';

  const generatePdf = async () => {
    setLoading(true);
    const {data, isError, isLoading} = await fetchApplicant();

    if (isError) {
      setLoading(false);
      dispatchMessage(t('shared.fetchingData.error'), 'error');
      return;
    }

    if (data && !isLoading && !isError) {
      const MyDocument = <ScreeningApplicantPDFReport reportData={data} t={t} currentLanguage={currentLanguage} />;

      const generatePdf = async () => {
        const blob = await pdf(MyDocument).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${data.firstName}_${data.lastName}_Empion_Report_${
          data.jobPosting
        }_${transformCurrentDateToDDMMYYYY()}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        setLoading(false);
      };

      generatePdf();
    }
  };

  const applicantData: ScreeningApplicant = {
    id,
    resourceId: id,
    firstName,
    lastName,
    email,
    sendViaEmail,
    jobPosting,
    location,
    companyDepartmentId,
    talentSurvey,
  };

  const getStatus = (talentSurvey: ScreeningApplicantTalentSurvey): string => {
    if (talentSurvey.completedAt) {
      return 'completed';
    } else if (talentSurvey.startedAt) {
      return 'assessmentStarted';
    } else if (talentSurvey.createdAt) {
      return 'inviteCreated';
    }
    return 'unknown';
  };

  return (
    <BasicTableRow>
      <StyledCell>
        <TextOverflowWithTooltip
          variant='body1'
          text={firstName}
          sxTypography={{fontWeight: 'bold', fontSize: '14px'}}
        />
      </StyledCell>
      <StyledCell>
        <TextOverflowWithTooltip sxTypography={{fontSize: '14px'}} variant='body1' text={jobPosting} />
      </StyledCell>
      <StyledCell>
        <TextOverflowWithTooltip sxTypography={{fontSize: '14px'}} variant='body1' text={location} />
      </StyledCell>
      <StyledCell>
        <Typography sx={{fontSize: '14px'}} variant='body1'>
          {tr(getStatus(talentSurvey))}
        </Typography>
      </StyledCell>
      <StyledCell>
        <TextOverflowWithTooltip sxTypography={{fontSize: '14px'}} variant='body1' text={daysElapsed} />
      </StyledCell>
      <StyledCell>
        {talentSurvey.culturalFit ? <CultureFit percentage={Math.round(talentSurvey.culturalFit)} /> : null}
      </StyledCell>
      <StyledCell>
        <Grid container width='100%' justifyContent='flex-end'>
          <Grid container gap='10px'>
            {getStatus(talentSurvey) === 'completed' ? (
              <EmpionButton
                onClick={generatePdf}
                disabled={getStatus(talentSurvey) !== 'completed' || loading}
                sx={{width: 'auto', minWidth: '32px', height: '32px', padding: 0}}
              >
                <Icon iconColor='currentColor' icon={IconType.DownloadArrow} />
              </EmpionButton>
            ) : (
              <Grid sx={{minWidth: '32px', height: '32px'}} />
            )}
            <ScreeningBurgerActions applicantData={applicantData} refetch={refetch} />
          </Grid>
        </Grid>
      </StyledCell>
    </BasicTableRow>
  );
};
